import axios from 'axios';

import config from '../config/env.js';
import { bumpWriteEpoch } from '../redux/requestDedupe.js';
import { installApiStats } from './apiStats.js';
import { getAuthToken } from './authUtils.js';
import { logErrorToSlack } from './logger.js';

const baseURL = config.apiBaseUrl;

/**
 * Axios instance configured with the base URL.
 */
const instance = axios.create({
  baseURL,
});

// Dev-only request counter on `window.__apiStats`; no-ops in production.
installApiStats(instance);

export const VIEW_AS_BLOCKED_MESSAGE = 'Action not allowed in View As mode.';

/**
 * Builds the synthetic rejection used to block writes while impersonating.
 *
 * Tagged so the response interceptor can tell it apart from a real transport
 * failure - it never left the browser, so there is nothing to report to Slack.
 *
 * @returns {Error} the tagged rejection
 */
const viewAsBlockedError = () => {
  const error = new Error(VIEW_AS_BLOCKED_MESSAGE);
  error.isViewAsBlocked = true;
  return error;
};

/**
 * Endpoints that use a write method but change nothing a read can observe.
 *
 * They must not invalidate in-flight read sharing: analytics fires on nearly
 * every interaction and `/log` fires on every failure, so treating them as
 * mutations would keep the dedupe permanently disarmed - exactly during the
 * mount storms it exists to collapse.
 */
const NON_INVALIDATING_ENDPOINTS = ['/us/events', '/log'];

/**
 * True when the request is going to our own API rather than a third party.
 *
 * Relative urls resolve against `baseURL`; the only absolute urls in the app
 * are third-party (ipify), and they must never receive our bearer token.
 *
 * @param {string} [url] the request url
 * @returns {boolean} whether the token may be attached
 */
const isOwnApiRequest = (url) => {
  if (!url) return true;
  if (!/^https?:\/\//i.test(url)) return true;
  return Boolean(baseURL) && url.startsWith(baseURL);
};

/**
 * Per-request opt-out from the centralized bearer token.
 *
 * A handful of endpoints establish the session rather than consume it, so they
 * must reach the API as genuinely anonymous requests - they carry their own
 * credential in the body or query and a stale bearer token from a previous
 * session would misidentify the caller. Passing `{ skipAuth: true }` in the
 * request config is the only supported way to suppress the header; setting a
 * blank `Authorization` would satisfy the guard below but still put the header
 * on the wire, which is what we are trying to avoid.
 *
 * The flag is read and removed inside the interceptor so it never survives into
 * anything axios derives from the config afterwards.
 *
 * @example
 * axios.post('/us/users', body, { skipAuth: true });
 */
instance.interceptors.request.use(
  async (config) => {
    const isViewAsMode = localStorage.getItem('isViewAsMode') === 'true';

    const readOnlyEndpoints = [
      '/list',
      '/search',
      '/overview',
      '/map-users',
      '/discovery-tree',
      '/scrape/company/employees',
      '/interactions/v3',
      '/interactions/status',
    ];
    const isReadOnlyPost =
      config.method?.toLowerCase() === 'post' &&
      readOnlyEndpoints.some((ep) => config.url?.includes(ep));

    // Block mutations if in View As mode
    if (
      isViewAsMode &&
      config.method &&
      config.method.toLowerCase() !== 'get' &&
      !isReadOnlyPost
    ) {
      return Promise.reject(viewAsBlockedError());
    }

    // Any real mutation invalidates in-flight read sharing, so a refetch
    // issued after a write never resolves from a request that predates it.
    const isMutation =
      config.method &&
      config.method.toLowerCase() !== 'get' &&
      !isReadOnlyPost &&
      !NON_INVALIDATING_ENDPOINTS.some((ep) => config.url?.includes(ep));
    if (isMutation) {
      bumpWriteEpoch();
    }

    // Centralized auth. Every thunk used to open its own `onAuthStateChanged`
    // subscription and await `getIdToken()` purely to build this one header.
    // An explicit header still wins, so thunks that have not been migrated
    // yet keep working unchanged, and `skipAuth` opts a request out entirely.
    const { skipAuth } = config;
    delete config.skipAuth;

    if (
      !skipAuth &&
      !config.headers?.Authorization &&
      isOwnApiRequest(config.url)
    ) {
      const token = await getAuthToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

instance.interceptors.response.use(
  (res) => {
    return res;
  },
  (err) => {
    // A cancelled request is a request we deliberately saved. Reporting it
    // spends the request we just avoided - and the three components that use
    // `AbortController` cancel often.
    if (axios.isCancel(err)) {
      return Promise.reject(err);
    }
    // The View As block never reached the network, so there is no failure to
    // report; forwarding it also lets callers surface the real reason instead
    // of a generic "Failed to ..." string.
    if (err?.isViewAsBlocked) {
      return Promise.reject(err);
    }
    if (err?.config?.url !== 'https://api.ipify.org?format=json') {
      logErrorToSlack(
        err,
        err?.config?.headers?.Authorization?.split(' ')?.[1],
      );
    }
    // Must reject, not resolve. Resolving handed every caller an `AxiosError`
    // as though it were a response: `r.status` is undefined on an AxiosError
    // (axios only materializes it inside `toJSON()`), so every `status === 200`
    // guard fell through to its synthetic `throw new Error('Failed to ...')`
    // and every `catch (e) { e?.response?.data }` was dead code - the server's
    // real error body was discarded.
    return Promise.reject(err);
  },
);

export default instance;
